import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, ChevronRight, Layers } from 'lucide-react'
import { CourseForm } from '@/components/app/courses/CourseForm'
import { useCourse, useUpdateCourse } from '@/hooks/useCourses'
import { CourseMutationPayload } from '@/services/coursesService'
import { paths } from '@/routes/path'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

function toCourseFormState(course: CourseMutationPayload & { is_active?: boolean }) {
  return {
    programmeId: course.programmeId,
    departmentId: course.departmentId ?? undefined,
    title: course.title,
    description: course.description ?? undefined,
    code: course.code ?? '',
    credits: course.credits ?? undefined,
    instructorId: course.instructorId ?? undefined,
    startDate: course.startDate ?? undefined,
    endDate: course.endDate ?? undefined,
    level: course.level ?? undefined,
    semester: course.semester ?? undefined,
    is_active: course.is_active ?? true,
  }
}

export function CourseManagePage() {
  const { courseId } = useParams<{ courseId: string }>()
  const navigate = useNavigate()
  const [courseError, setCourseError] = useState<string | null>(null)

  const { data: course, isLoading: isCourseLoading, error: loadCourseError } = useCourse(courseId)
  const updateCourseMutation = useUpdateCourse(courseId || '')

  useEffect(() => {
    if (loadCourseError) {
      setCourseError((loadCourseError as Error).message || 'Unable to load course details.')
    } else {
      setCourseError(null)
    }
  }, [loadCourseError])

  const initialValues = useMemo(() => {
    if (!course) return undefined

    return toCourseFormState({
      programmeId: course.programmeId ?? '',
      departmentId: course.departmentId ?? undefined,
      title: course.title,
      description: course.description,
      code: course.code ?? '',
      credits: course.credits ?? undefined,
      instructorId: course.instructorId ?? undefined,
      startDate: course.startDate ?? undefined,
      endDate: course.endDate ?? undefined,
      level: course.level ?? undefined,
      semester: course.semester ?? undefined,
      is_active: course.isActive,
    })
  }, [course])

  if (!courseId) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center text-secondary-500">
        Course identifier is required.
      </div>
    )
  }

  if (isCourseLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center text-secondary-500">
        Loading course information…
      </div>
    )
  }

  if (!course) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center text-secondary-500">
        <p className="text-lg font-semibold">Course not found</p>
        <Button variant="link" className="text-primary-600 hover:text-primary-700" onClick={() => navigate(-1)}>
          Go back
        </Button>
      </div>
    )
  }

  const handleCourseSubmit = async (payload: CourseMutationPayload) => {
    setCourseError(null)
    try {
      await updateCourseMutation.mutateAsync(payload)
    } catch (err) {
      setCourseError((err as Error).message || 'Unable to update course.')
    }
  }

  const goToModules = () => {
    navigate(paths.app.courses.modules.root.build(course.id))
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-sm text-secondary-500">
            <Link
              to={paths.app.courses.root.absolute}
              className="inline-flex items-center gap-2 hover:text-secondary-700"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to courses
            </Link>
            <ChevronRight className="h-4 w-4" />
            <span className="inline-flex items-center gap-1">
              <Layers className="h-4 w-4" />
              Manage Course
            </span>
          </div>
          <h1 className="text-3xl font-bold text-secondary-900">{course.title}</h1>
          <p className="text-secondary-500">Update course details and keep everything in sync with learners.</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button variant="outline" onClick={() => navigate(paths.app.courses.detail.build(course.id))}>
            Preview Course
          </Button>
          <Button variant="outline" onClick={() => navigate(paths.app.courses.content.build(course.id))}>
            View Learning Experience
          </Button>
          <Button className="bg-primary-600 text-white hover:bg-primary-700" onClick={goToModules}>
            Manage Modules
          </Button>
        </div>
      </div>

      {courseError && (
        <div className="rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-600">
          {courseError}
        </div>
      )}

      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle>Course Details</CardTitle>
        </CardHeader>
        <CardContent>
          <CourseForm
            initialValues={initialValues}
            onSubmit={handleCourseSubmit}
            submitLabel={updateCourseMutation.isPending ? 'Saving…' : 'Save Changes'}
            isSubmitting={updateCourseMutation.isPending}
          />
        </CardContent>
      </Card>

      <Card className="border border-dashed border-secondary-200 bg-secondary-50/40">
        <CardHeader>
          <CardTitle className="text-base font-semibold text-secondary-800">Ready to organise modules?</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <p className="text-sm text-secondary-600">
            Head to the dedicated modules workspace to reorder modules, upload resources, and set the learning pace.
          </p>
          <Button onClick={goToModules} variant="secondary" className="md:w-auto">
            Go to Modules
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
